import { ChevronDown, LogOut, Settings } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import { Dropdown, DropdownDivider, DropdownItem } from './Dropdown.tsx'
import './UserProfileDropdown.css'

export function initials(name: string): string {
  return (
    name
      .split(/[\s@.]+/)
      .filter(Boolean)
      .map((p) => p[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?'
  )
}

/**
 * The account menu, design.md 8.8, composed from the shared Dropdown. A fixed header block,
 * the app's own rows (`children`), then Sign Out as a plain red row (8.11), never a filled
 * button. `first` rows come before everything else; `onSettings` adds the Settings row (8.8).
 * ICE has no profile page, so no Profile row.
 */
export function UserProfileDropdown({
  name,
  email,
  signOutHref,
  first,
  onSettings,
  children,
}: {
  name: string
  email: string | null
  signOutHref: string
  first?: ReactNode
  onSettings?: () => void
  children?: ReactNode
}) {
  return (
    <Dropdown
      align="end"
      panelClassName="profile-panel"
      trigger={({ open, toggle }) => (
        <button type="button" className="profile-trigger" onClick={toggle} aria-expanded={open} aria-haspopup="menu">
          <span className="avatar" aria-hidden="true">{initials(name)}</span>
          <span className="profile-trigger-name">{name.split(' ')[0]}</span>
          <ChevronDown aria-hidden="true" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease-out' }} />
        </button>
      )}
    >
      <div className="profile-header">
        <span className="avatar lg" aria-hidden="true">{initials(name)}</span>
        <div className="profile-text">
          <div className="profile-name">{name}</div>
          {email && <div className="profile-email">{email}</div>}
        </div>
      </div>
      <DropdownDivider />
      {/* Each row is set apart from the next. */}
      {[
        first,
        onSettings && (
          <DropdownItem icon={Settings} onClick={onSettings}>
            Settings
          </DropdownItem>
        ),
        children,
      ]
        .filter(Boolean)
        .map((row, i) => (
          <Fragment key={i}>
            {row}
            <DropdownDivider />
          </Fragment>
        ))}
      <DropdownItem icon={LogOut} destructive href={signOutHref}>
        Sign Out
      </DropdownItem>
    </Dropdown>
  )
}
